import type { ReactNode } from "react";
import { Link } from "react-router";
import { PublicPageLayout } from "@/components/PublicPageLayout";

const TEXT_LINK =
  "font-medium text-[var(--cs-navy)] underline decoration-[rgba(12,63,107,0.25)] underline-offset-4 hover:decoration-[rgba(12,63,107,0.55)]";

function TermsSection({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="scroll-mt-28 border-t border-[#DCE4EA] py-9 sm:py-11">
      <div className="grid gap-4 sm:grid-cols-[44px_minmax(0,1fr)] sm:gap-5">
        <span className="text-[13px] font-medium tabular-nums text-[#8291A3]">{number}</span>
        <div>
          <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.02em] text-[var(--cs-navy)]">
            {title}
          </h2>
          <div className="mt-5 space-y-4 text-[15px] leading-7 text-[#536274]">{children}</div>
        </div>
      </div>
    </section>
  );
}

export function TermsPage() {
  return (
    <PublicPageLayout>
      <article className="mx-auto w-full max-w-[900px] text-[var(--cs-navy)]">
        <header className="pb-10 sm:pb-14">
          <h1 className="text-[42px] font-semibold leading-[1.04] tracking-[-0.04em] sm:text-[58px]">
            Gebruiks<wbr />voorwaarden
          </h1>
          <p className="mt-4 text-[13px] text-[#6E7D8F]">
            <time dateTime="2026-10-04">4 oktober 2026</time>
          </p>
          <p className="mt-8 max-w-[800px] text-[17px] leading-8 text-[#536274]">
            Deze voorwaarden regelen het professionele gebruik van de websites,
            applicaties, platformen en diensten van Carmen (samen de “Diensten”). Ze vormen
            samen met uw offerte, bestelbon of klantovereenkomst de afspraken tussen u en
            Carmen.
          </p>
        </header>

        <TermsSection number="01" title="Aanbieder, toepassing en aanvaarding">
          <p>
            De Diensten zijn bedoeld voor zorgverleners, praktijken en zorgorganisaties. Door
            een account aan te maken of een aanbod te aanvaarden, bevestigt u dat u
            professioneel handelt en, als u namens een praktijk of organisatie handelt,
            bevoegd bent om die te verbinden. U aanvaardt daarbij deze voorwaarden.
          </p>
          <p>
            Wie uitsluitend als consument handelt, kan geen Carmen-account aanmaken of een
            abonnement afsluiten. Als iemand toch wettelijk als consument kwalificeert,
            blijven alle dwingende consumentenrechten gelden.
          </p>
          <p>
            Carmen wordt aangeboden door Carmen Labs BV, gevestigd in Gent, België,
            ondernemingsnummer en btw-nummer BE 1043.364.959. Vragen kunt u sturen naar{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            .
          </p>
          <p>
            Een individuele klantovereenkomst of bestelbon gaat bij tegenstrijdigheid voor
            op deze voorwaarden. Voor verwerking van persoonsgegevens gaat de
            verwerkersovereenkomst voor.
          </p>
        </TermsSection>

        <TermsSection number="02" title="Dienst, AI en menselijke controle">
          <p>
            Carmen gebruikt artificiële intelligentie voor medische documentatiefuncties.
            De resultaten zijn concepten en kunnen fouten of onvolledigheden bevatten.
          </p>
          <p>
            Een bevoegde zorgverlener controleert en corrigeert elk resultaat vóór gebruik
            in een patiëntendossier, verslag, voorschrift of communicatie. Carmen is een
            hulpmiddel en is niet bestemd voor spoedeisende hulp of autonome klinische
            besluitvorming. Deze controleplicht beperkt de eigen wettelijke en contractuele
            verplichtingen van Carmen niet.
          </p>
          <p>
            Carmen biedt redelijke productinformatie en instructie; de klant zorgt daarnaast
            voor bevoegde, passend opgeleide gebruikers, AI-geletterdheid en menselijk
            toezicht binnen de eigen zorgcontext.
          </p>
        </TermsSection>

        <TermsSection number="03" title="Account en toegestaan gebruik">
          <p>
            U verstrekt correcte accountgegevens, houdt authenticatiemiddelen persoonlijk en
            vertrouwelijk en meldt een vermoeden van misbruik of een beveiligingsincident
            onmiddellijk. De klant bepaalt wie toegang krijgt tot het account.
          </p>
          <p>
            Wanneer de klant Carmen installeert of gebruikt in combinatie met software of
            systemen van derden, bevestigt de klant dat hij daartoe bevoegd is onder de
            toepasselijke licenties en overeenkomsten. De klant verkrijgt vooraf alle eventueel
            vereiste toestemmingen van de leverancier of een andere bevoegde partij voor de
            overeengekomen invoer- en herkenningsfuncties en instrueert Carmen niet tot gebruik
            dat deze rechten of toestemmingen overschrijdt.
          </p>
          <p>Carmen mag niet worden gebruikt om:</p>
          <ul className="list-disc space-y-2 pl-5 marker:text-[#8DA0B4]">
            <li>wetgeving, beroepsgeheim of rechten van anderen te schenden;</li>
            <li>gegevens te verwerken waarvoor geen geldige bevoegdheid of grondslag bestaat;</li>
            <li>beveiliging, toegangscontrole of gebruikslimieten te omzeilen;</li>
            <li>
              de Dienst te verstoren, misbruiken, kopiëren of reverse-engineeren, behalve
              voor zover dwingend recht observatie, onderzoek, testen, decompilatie of
              interoperabiliteit uitdrukkelijk toestaat.
            </li>
          </ul>
        </TermsSection>

        <TermsSection number="04" title="Gegevens, vertrouwelijkheid en eigendom">
          <p>
            De klant blijft rechthebbende op de ingevoerde gegevens en eigen inhoud. De klant
            geeft Carmen alleen de beperkte, niet-exclusieve rechten die nodig zijn om de
            Diensten volgens de afspraken en gedocumenteerde instructies te leveren,
            beveiligen en ondersteunen. Tussen de partijen maakt Carmen geen aanspraak op
            eigendom van de gegenereerde output. Voor zover Carmen daarop overdraagbare
            rechten heeft, mag de klant die output blijvend gebruiken, aanpassen en delen
            voor de eigen beroepsactiviteiten. Klantinhoud wordt niet gebruikt om AI-modellen
            te trainen.
          </p>
          <p>
            De klant, een zelfstandige arts, praktijk of zorgorganisatie, is doorgaans
            verwerkingsverantwoordelijke voor medische gegevens en Carmen treedt daarbij op
            als verwerker. Partijen sluiten voor die verwerking een verwerkersovereenkomst
            conform artikel 28 AVG, die integraal deel uitmaakt van hun afspraken. Carmen is
            waar toepasselijk zelf
            verwerkingsverantwoordelijke voor accountbeheer, facturatie en beveiliging. De
            klant staat in voor een geldige grondslag, gepaste informatie aan betrokkenen en
            naleving van het beroepsgeheim. Meer informatie staat in het{" "}
            <Link to="/privacy" className={TEXT_LINK}>
              Privacybeleid
            </Link>
            , het{" "}
            <Link to="/trust" className={TEXT_LINK}>
              Trust Center
            </Link>{" "}
            en het{" "}
            <Link to="/trust#documents" className={TEXT_LINK}>
              DPA-pakket
            </Link>
            .
          </p>
          <p>
            Carmen behoudt de rechten op de software, modellen, vormgeving, documentatie en
            merknaam. Deze voorwaarden dragen geen intellectuele-eigendomsrechten over.
          </p>
          <p>
            Beide partijen beschermen vertrouwelijke informatie met passende zorg en
            gebruiken die alleen voor de uitvoering van hun afspraken. Ze delen die alleen
            met personen die ze nodig hebben en aan een gelijkwaardige geheimhoudingsplicht
            zijn gebonden. De plicht geldt niet voor informatie die rechtmatig openbaar of al
            bekend was, onafhankelijk werd ontwikkeld of rechtmatig van een derde kwam. Een
            wettelijk verplichte bekendmaking is toegestaan nadat de andere partij, waar
            toegelaten, vooraf is geïnformeerd. Deze plicht geldt vijf jaar na beëindiging en
            voor persoonsgegevens, patiëntinformatie en bedrijfsgeheimen zolang hun aard of de
            wet dat vereist.
          </p>
        </TermsSection>

        <TermsSection number="05" title="Abonnement, betaling en opzegging">
          <p>
            Het gekozen plan, de prijs, looptijd en eventuele gebruikslimieten staan bij de
            bestelling of in de klantovereenkomst. Prijzen voor professionele klanten zijn
            exclusief btw, tenzij anders vermeld. Een abonnement wordt verlengd volgens de
            gekozen facturatieperiode en kan vóór de volgende verlenging worden opgezegd.
          </p>
          <p>
            De bestelling vermeldt ook een eventuele proefperiode, automatische omzetting,
            uiterste opzegdatum en de betaalwijze. Carmen meldt een prijsverhoging voor een
            volgende periode minstens dertig dagen vooraf. De klant kan vóór de ingangsdatum
            zonder bijkomende vergoeding opzeggen. Reeds aangevatte periodes worden niet pro
            rata terugbetaald, behalve wanneer de klantovereenkomst of dwingend recht dat
            vereist.
          </p>
        </TermsSection>

        <TermsSection number="06" title="Beschikbaarheid en wijzigingen aan de Dienst">
          <p>
            Carmen levert de Dienst met redelijke zorg en streeft naar betrouwbare
            beschikbaarheid. Onderhoud, beveiligingsmaatregelen, storingen en afhankelijkheid
            van derden kunnen de werking tijdelijk beïnvloeden. Een beschikbaarheidsgarantie
            geldt alleen wanneer die afzonderlijk schriftelijk is afgesproken.
          </p>
          <p>
            Functies kunnen worden aangepast wegens veiligheid, gewijzigde wetgeving,
            technische noodzaak of productverbetering. Bij een wezenlijk nadelige wijziging
            ontvangt de klant minstens dertig dagen vooraf bericht via een duurzaam kanaal en
            kan hij vóór de ingangsdatum zonder bijkomende vergoeding opzeggen. Een dringende
            beveiligings- of wettelijke wijziging kan onmiddellijk ingaan; Carmen licht de
            klant dan zo snel mogelijk toe.
          </p>
        </TermsSection>

        <TermsSection number="07" title="Export, overstap en verwijdering">
          <p>
            De klant kan de accountgegevens, instellingen, het gebruikslogboek en de
            bewaarde opnames met transcripties exporteren in een gangbaar, machineleesbaar
            formaat. Niet exporteerbaar zijn interne beveiligingsgegevens, geanonimiseerde
            of geaggregeerde gegevens en de software, modellen en bedrijfsgeheimen van
            Carmen. Deze uitzonderingen beletten of vertragen de overstap niet.
          </p>
          <p>
            Op verzoek ondersteunt Carmen een overstap naar een andere dienst of eigen
            infrastructuur, in lijn met de EU Data Act waar die van toepassing is. Voor
            standaardexport rekent Carmen geen aparte vergoeding aan.
          </p>
          <p>
            Gebruikers met een rechtstreeks account kunnen dat op elk moment zelf verwijderen
            via de accountinstellingen. Een account dat aan een organisatie is gekoppeld,
            wordt verwijderd via de organisatiebeheerder of via{" "}
            <a href="mailto:info@askcarmen.eu" className={TEXT_LINK}>
              info@askcarmen.eu
            </a>
            .
          </p>
        </TermsSection>

        <TermsSection number="08" title="Opschorting en beëindiging">
          <p>
            Carmen kan toegang beperken of opschorten bij een ernstig beveiligingsrisico,
            misbruik, betalingsachterstand of een wezenlijke contractbreuk. Wanneer de
            situatie dat toelaat, krijgt de klant vooraf bericht en een redelijke termijn om
            het probleem te herstellen.
          </p>
          <p>
            Beëindigt Carmen een betaalde Dienst zonder tekortkoming van de klant, dan geldt
            een opzegtermijn van minstens dertig dagen en krijgt de klant een redelijke
            mogelijkheid tot export. Een onmiddellijke beperking blijft mogelijk wanneer dat
            nodig is om patiënten, gegevens of de Dienst te beschermen.
          </p>
          <p>
            Na beëindiging vervalt het gebruiksrecht. Export, bewaring en verwijdering van
            gegevens verlopen volgens artikel 7, het Privacybeleid en de toepasselijke
            verwerkersovereenkomst.
          </p>
        </TermsSection>

        <TermsSection number="09" title="Aansprakelijkheid">
          <p>
            Iedere partij is aansprakelijk voor de rechtstreekse schade die zij door een
            toerekenbare contractuele tekortkoming veroorzaakt. Carmen is niet aansprakelijk
            voor indirecte schade of gevolgschade, voor zover de wet die beperking toestaat.
            Wanneer schade mede voortvloeit uit gebruik buiten het beoogde doel of uit het
            achterwege laten van de vereiste menselijke controle, wordt de aansprakelijkheid
            verminderd in de mate waarin dat aan de klant kan worden toegerekend. Dit sluit
            aansprakelijkheid van Carmen voor een eigen tekortkoming of gebrekkige Dienst niet
            uit.
          </p>
          <p>
            De totale aansprakelijkheid van Carmen is beperkt tot de vergoedingen die voor de
            betrokken Diensten zijn betaald of verschuldigd in de twaalf maanden vóór het
            schadegeval. Deze beperking geldt niet bij bedrog, opzet of zware fout, bij
            aantasting van leven of lichamelijke integriteit, of wanneer aansprakelijkheid
            wettelijk niet kan worden beperkt of wanneer zij een essentiële verbintenis zou
            uithollen. De verwerkersovereenkomst regelt de onderlinge aansprakelijkheid voor
            persoonsgegevens, zonder afbreuk te doen aan de rechten van betrokkenen uit
            artikel 82 AVG. Niets in deze voorwaarden beperkt niet-uitsluitbare
            productaansprakelijkheid.
          </p>
        </TermsSection>

        <TermsSection number="10" title="Toepasselijk recht en wijzigingen">
          <p>
            Belgisch recht is van toepassing. Geschillen worden voorgelegd aan de volgens
            Belgisch recht bevoegde rechtbank, tenzij dwingend recht of een individuele
            klantovereenkomst anders bepaalt.
          </p>
          <p>
            Bij een wezenlijke wijziging van deze voorwaarden informeert Carmen de klant
            minstens dertig dagen vóór de ingangsdatum via een duurzaam kanaal. De klant kan
            vóór die datum zonder bijkomende vergoeding opzeggen. Waar de wet of de aard van
            de wijziging dat vereist, vraagt Carmen opnieuw uitdrukkelijke aanvaarding. Als
            een bepaling ongeldig blijkt, blijven de overige bepalingen gelden.
          </p>
        </TermsSection>
      </article>
    </PublicPageLayout>
  );
}
